import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Loader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", description: "", week_number: 1, session_number: 1, date: "", time: "", meeting_link: "", replay_url: "", takeaways: "" };

export default function Sessions() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["sessions"], queryFn: async () => (await api.get("/sessions")).data });

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    const payload = { ...form, week_number: Number(form.week_number), session_number: Number(form.session_number), takeaways: form.takeaways ? form.takeaways.split("\n").filter(Boolean) : [] };
    try {
      if (editId) await api.put(`/sessions/${editId}`, payload); else await api.post("/sessions", payload);
      toast.success("Session saved!"); setOpen(false); setForm(empty); setEditId(null);
      qc.invalidateQueries({ queryKey: ["sessions"] });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const edit = (s) => { setForm({ ...s, takeaways: (s.takeaways || []).join("\n") }); setEditId(s.id); setOpen(true); };
  const del = async (id) => { await api.delete(`/sessions/${id}`); qc.invalidateQueries({ queryKey: ["sessions"] }); toast.success("Deleted"); };

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-5" data-testid="sessions-page">
      <div className="flex items-center justify-between">
        <h2 className="font-head text-3xl text-csh-blue">Sessions 📅</h2>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditId(null); } }}>
          <DialogTrigger asChild><Button className="rounded-full gradient-purple text-white" data-testid="add-session-btn"><Icons.Plus size={18} /> New Session</Button></DialogTrigger>
          <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-head text-2xl text-csh-blue">{editId ? "Edit" : "New"} Session</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Session title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl border-2 py-6" data-testid="session-title" />
              <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl border-2" />
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" placeholder="Week #" value={form.week_number} onChange={(e) => setForm({ ...form, week_number: e.target.value })} className="rounded-xl border-2 py-6" />
                <Input type="number" placeholder="Session #" value={form.session_number} onChange={(e) => setForm({ ...form, session_number: e.target.value })} className="rounded-xl border-2 py-6" />
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl border-2 py-6" />
                <Input placeholder="Time e.g. 5:00 PM" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="rounded-xl border-2 py-6" />
              </div>
              <Input placeholder="Zoom / Meeting link" value={form.meeting_link} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} className="rounded-xl border-2 py-6" />
              <Input placeholder="Replay URL (YouTube ID or link)" value={form.replay_url} onChange={(e) => setForm({ ...form, replay_url: e.target.value })} className="rounded-xl border-2 py-6" />
              <Textarea placeholder="Takeaways (one per line)" value={form.takeaways} onChange={(e) => setForm({ ...form, takeaways: e.target.value })} className="rounded-xl border-2" />
              <Button onClick={save} className="w-full rounded-full gradient-orange py-6 font-bold text-white" data-testid="save-session-btn">Save Session</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {(!data || data.length === 0) ? <EmptyState icon="CalendarDays" title="No sessions" /> : (
        <div className="grid gap-3 md:grid-cols-2">
          {data.map((s) => (
            <div key={s.id} className="rounded-3xl bg-white p-5 shadow-sm border border-black/5" data-testid={`session-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-bold uppercase text-muted-foreground">Week {s.week_number} · Session {s.session_number}</div>
                  <h3 className="font-head text-xl text-csh-blue">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.date} · {s.time}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => edit(s)} className="rounded-lg p-2 hover:bg-black/5" data-testid={`edit-session-${s.id}`}><Icons.Pencil size={16} className="text-csh-purple" /></button>
                  <button onClick={() => del(s.id)} className="rounded-lg p-2 hover:bg-black/5"><Icons.Trash2 size={16} className="text-red-500" /></button>
                </div>
              </div>
              <div className="mt-2 flex gap-2 text-xs">
                {s.meeting_link && <span className="rounded-full bg-blue-100 px-2 py-0.5 font-bold text-blue-700">Zoom ✓</span>}
                {s.replay_url && <span className="rounded-full bg-purple-100 px-2 py-0.5 font-bold text-purple-700">Replay ✓</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
